import { Box, FormControl, Grid, InputLabel, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { Control, Controller, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useUser } from "../../Hooks/useUser";
import { setHasMembership, setPayAmount } from "../../Redux/slices/JobPostingCreationSlice";
import { useAppDispatch, useAppSelector } from "../../Redux/store";
import { JobConstants, PaymentType, SalaryType } from "../../Types/Job";
import { KaziSwitch } from "../Kazi/KaziSwitch";
import { TalentPreferenceType } from "../../Schemas/JobPostingSchemas";

interface IProps {
    jobType: string;
}

const PaymentPreferences = ({ jobType }:IProps) => {
    /* ******************** Hooks ******************** */
    const { hasPremium } = useUser();
    const dispatch = useAppDispatch();
    const {
        control,
        formState: { errors },
        trigger,
        watch,
        setValue,
        getValues
    } = useFormContext<TalentPreferenceType>();
    /* ******************** Variables ******************** */
    const payAmount = useAppSelector( state => state.JobPostingCreation.pay_amount);
    const payTypes = useMemo(() => {
        let paymentTypes = PaymentType;
        if (jobType === 'Short-term') paymentTypes = PaymentType.filter( t => t == 'One-time');
        if (jobType === 'Full-time' || jobType === 'Internship') {
            dispatch(setHasMembership(hasPremium))
            paymentTypes = PaymentType.filter( t => t !== 'One-time')
        } else {
            dispatch(setHasMembership(true));
        }
        return paymentTypes
    }, [jobType])
    const watchPayAmount = watch('pay_amount');
    const maxError = useMemo(() => {
        const err = errors?.pay_amount?.max;
        if (err) {
            if (err.type === 'is-greater') {
                return err.message + ` ${getValues('pay_amount.min')}`;
            } 
            return err.message;
        }
        return ''
    }, [errors.pay_amount?.max]);
    /* ******************** Functions ******************** */
    const revalidateMax = () => {
        // console.log('Validating Max');
        trigger('pay_amount.max');
    }
    const onChecked = () => {
        let newType = payAmount.pay_type === JobConstants.SalaryType.Fixed ? JobConstants.SalaryType.Range : JobConstants.SalaryType.Fixed;
        setValue('pay_amount.pay_type', newType);
        dispatch(setPayAmount({ ...payAmount, pay_type: newType as SalaryType}));
        revalidateMax();
    }
    const isShortTerm = jobType === 'Short-term' || jobType === '';
    const isFixed = payAmount.pay_type === JobConstants.SalaryType.Fixed;
    /* ******************** Effects ******************** */
    useEffect(() =>{
        if (!isShortTerm && !isFixed) {
            dispatch(setPayAmount({ ...payAmount, pay_type: JobConstants.SalaryType.Fixed as SalaryType}));
        }
    }, [jobType])
    useEffect(() => {
        revalidateMax();
    }, [watchPayAmount.max, trigger])
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                        <Box
                            display={'flex'}
                            flexDirection={'row'}
                            gap={2}
                            marginBottom={1}
                            height={'20px'}
                        >
                            <Typography variant='body-small' color={'black.200'}>Payment{jobType === 'Short-term' && '*'}</Typography> 
                            {/* {isShortTerm && (<KaziSwitch 
                                leftLabel="Fixed"
                                rightLabel="Range"
                                size="small"
                                checked={payAmount.pay_type === JobConstants.SalaryType.Range}
                                onChange={onChecked}
                                sx={{
                                    fontSize: "10px"
                                }}
                            />)} */}
                        </Box>
                        <Box
                            display={'flex'}
                            flexDirection={'row'}
                            gap={2}
                        >
                            <Controller
                                name='pay_amount.min'
                                control={control}
                                render={({field}) =>(
                                    <NumericFormat
                                        {...field}
                                        sx={{ flex: 1}}
                                        customInput={TextField}
                                        thousandSeparator={','}
                                        prefix={'$'}
                                        placeholder={'Payment amount'}
                                    />
                                )}
                            />
                            {!isFixed && (
                                <>
                                    <Typography variant='body-small' color={'black.200'} marginBottom={1} alignSelf={'center'}>-</Typography>
                                    <Controller
                                        name='pay_amount.max'
                                        control={control}
                                        render={({field}) =>(
                                            <Box flexDirection={'column'} flex={1}>
                                                <NumericFormat
                                                    {...field}
                                                    // @ts-ignore 
                                                    error={maxError.length > 0}
                                                    sx={{ flex: 1}}
                                                    customInput={TextField}
                                                    thousandSeparator={','}
                                                    prefix={'$'}
                                                    placeholder={'Payment amount'}
                                                />
                                                {/* @ts-ignore */}
                                                {maxError.length > 0  && <Typography variant="body-smaller" color='error' >{maxError}</Typography>}
                                            </Box>
                                        )}
                                    />
                                </>
                            )} 
                        </Box>
                    </Grid>                           
                    <Grid  item display={"flex"} flexDirection={"column"} width={'150px'}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Payment type</Typography> 
                        <Controller
                            name='pay_rate'
                            control={control}
                            render={({field}) =>(                                         
                                <FormControl>   
                                    <InputLabel>{field.value ?"":"Payment type"}</InputLabel>
                                    <TextField
                                        {...field}
                                        select
                                    >
                                        {
                                            payTypes.map((payment) => (
                                                <MenuItem key={payment} value={payment}>
                                                    {payment}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </FormControl>
                            )}
                        />
                    </Grid>
        </>
    )
}

export default PaymentPreferences